/**
 * Smart Search Utility
 * Powerful, fuzzy search with synonyms, semantic understanding, and intelligent matching
 */

// Common synonyms and related terms for search
const SYNONYMS: Record<string, string[]> = {
  // Vehicle related
  'car': ['vehicle', 'auto', 'automobile', 'ride'],
  'vehicle': ['car', 'auto', 'automobile', 'ride'],
  'vin': ['vehicle identification number', 'vin number', 'vehicle number', 'car number'],
  'license': ['plate', 'registration', 'tag', 'licence'],
  'plate': ['license', 'registration', 'tag', 'licence plate'],
  'tesla': ['ev', 'electric', 'electric vehicle'],
  'toyota': ['car'],
  'truck': ['vehicle', 'pickup'],
  'suv': ['vehicle', 'crossover'],

  // Insurance related
  'insurance': ['policy', 'coverage', 'insured', 'premium'],
  'policy': ['insurance', 'coverage'],
  'auto insurance': ['car insurance', 'vehicle insurance'],
  'home insurance': ['homeowners', 'house insurance', 'property insurance'],
  'health insurance': ['medical insurance', 'healthcare coverage'],

  // Healthcare related
  'doctor': ['physician', 'doc', 'dr', 'medical', 'healthcare', 'provider'],
  'dentist': ['dental', 'teeth', 'orthodontist'],
  'eye': ['vision', 'optometrist', 'ophthalmologist', 'optical'],
  'hospital': ['medical center', 'clinic', 'healthcare facility'],

  // Home related
  'home': ['house', 'residence', 'property', 'address'],
  'address': ['location', 'place', 'home', 'house'],
  'plumber': ['plumbing', 'pipes', 'water', 'leak'],
  'electrician': ['electrical', 'electric', 'wiring', 'power'],
  'hvac': ['heating', 'cooling', 'air conditioning', 'ac', 'furnace'],

  // Finance related
  'bank': ['banking', 'financial', 'finance', 'account'],
  'credit': ['credit card', 'card'],
  'mortgage': ['loan', 'home loan'],

  // Utilities
  'electric': ['electricity', 'power', 'energy'],
  'water': ['water bill', 'utility'],
  'gas': ['natural gas', 'utility'],
  'internet': ['wifi', 'broadband', 'network'],

  // Common query words
  'number': ['#', 'no', 'num'],
  'phone': ['call', 'telephone', 'contact'],
  'email': ['mail', 'contact'],
};

// Stop words to filter out (common words that don't add search value)
const STOP_WORDS = new Set([
  'what', 'is', 'my', 'the', 'a', 'an', 'of', 'for', 'to', 'in', 'on', 'at',
  'and', 'or', 'do', 'does', 'how', 'where', 'when', 'which', 'who', 'show',
  'me', 'find', 'get', 'can', 'i', 'have', 'has', 'with', 'from', 'this',
  'that', 'it', 'be', 'are', 'was', 'were', 'been', 'being', 'will', 'would',
  'could', 'should', 'may', 'might', 'must', 'shall', 'about', 'all', 'also',
  'any', 'some', 'please', 'tell', 'give', 'need', 'want', 'like', 'know'
]);

export interface SearchMatch {
  score: number;
  matchedTerms: string[];
  matchType: 'exact' | 'partial' | 'synonym' | 'fuzzy';
}

/**
 * Tokenize a query into meaningful search terms
 */
export function tokenizeQuery(query: string): string[] {
  const lower = query.toLowerCase().trim();

  // Split by whitespace and punctuation
  const words = lower.split(/[\s,.\-_!?'"]+/).filter(w => w.length > 0);

  // Filter out stop words but keep meaningful short words
  const filtered = words.filter(word => {
    // Keep words 3+ chars that aren't stop words
    if (word.length >= 3 && !STOP_WORDS.has(word)) return true;
    // Keep specific important short words
    if (['vin', 'car', 'ev', 'ac', 'dr'].includes(word)) return true;
    return false;
  });

  // If all words were filtered out, return original meaningful words
  if (filtered.length === 0) {
    return words.filter(w => w.length >= 2 && !STOP_WORDS.has(w));
  }

  return filtered;
}

/**
 * Expand search terms with synonyms
 */
export function expandWithSynonyms(terms: string[]): string[] {
  const expanded = new Set<string>(terms);

  for (const term of terms) {
    // Direct synonym lookup
    if (SYNONYMS[term]) {
      SYNONYMS[term].forEach(syn => expanded.add(syn));
    }

    // Partial match synonyms (e.g., "vin" should match "vehicle identification number")
    for (const [key, synonyms] of Object.entries(SYNONYMS)) {
      if (key.includes(term) || term.includes(key)) {
        expanded.add(key);
        synonyms.forEach(syn => expanded.add(syn));
      }
    }
  }

  return Array.from(expanded);
}

/**
 * Calculate Levenshtein distance for fuzzy matching
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;

  if (m === 0) return n;
  if (n === 0) return m;

  const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j - 1] + 1, // replace
          dp[i - 1][j] + 1,     // delete
          dp[i][j - 1] + 1      // insert
        );
      }
    }
  }

  return dp[m][n];
}

/**
 * Check if a term fuzzy matches a word
 */
function fuzzyMatch(term: string, word: string, threshold: number = 0.8): boolean {
  if (term.length < 3 || word.length < 3) return false;

  const distance = levenshteinDistance(term, word);
  const maxLen = Math.max(term.length, word.length);
  const similarity = 1 - (distance / maxLen);

  return similarity >= threshold;
}

/**
 * Smart search matching - checks if searchable text matches query terms
 */
export function smartMatch(searchableText: string, queryTerms: string[]): SearchMatch | null {
  const lowerText = searchableText.toLowerCase();
  const textWords = lowerText.split(/[\s,.\-_!?'"]+/).filter(w => w.length > 0);

  const matchedTerms: string[] = [];
  let totalScore = 0;
  let matchType: SearchMatch['matchType'] = 'partial';

  for (const term of queryTerms) {
    let termMatched = false;
    let termScore = 0;

    // 1. Exact phrase match (highest score)
    if (lowerText.includes(term)) {
      termMatched = true;
      termScore = 1.0;
      matchType = 'exact';
    }

    // 2. Word boundary match
    if (!termMatched) {
      for (const word of textWords) {
        if (word === term) {
          termMatched = true;
          termScore = 0.95;
          matchType = 'exact';
          break;
        }
        if (word.startsWith(term) || word.endsWith(term)) {
          termMatched = true;
          termScore = 0.85;
          matchType = 'partial';
          break;
        }
        if (word.includes(term) && term.length >= 3) {
          termMatched = true;
          termScore = 0.75;
          matchType = 'partial';
          break;
        }
      }
    }

    // 3. Synonym match
    if (!termMatched) {
      const synonyms = SYNONYMS[term] || [];
      for (const syn of synonyms) {
        if (lowerText.includes(syn)) {
          termMatched = true;
          termScore = 0.7;
          matchType = 'synonym';
          break;
        }
      }
    }

    // 4. Fuzzy match (for typos)
    if (!termMatched && term.length >= 3) {
      for (const word of textWords) {
        if (fuzzyMatch(term, word, 0.75)) {
          termMatched = true;
          termScore = 0.5;
          matchType = 'fuzzy';
          break;
        }
      }
    }

    if (termMatched) {
      matchedTerms.push(term);
      totalScore += termScore;
    }
  }

  // No matches found
  if (matchedTerms.length === 0) {
    return null;
  }

  // Calculate final score based on how many terms matched
  const matchRatio = matchedTerms.length / queryTerms.length;
  const finalScore = (totalScore / queryTerms.length) * matchRatio;

  return {
    score: finalScore,
    matchedTerms,
    matchType,
  };
}

/**
 * Extract meaningful keywords from user data for better searchability
 */
export function buildSearchableText(
  fields: (string | undefined | null)[],
  additionalKeywords: string[] = []
): string {
  const parts = fields
    .filter((f): f is string => !!f)
    .map(f => f.toLowerCase());

  return [...parts, ...additionalKeywords].join(' ');
}

/**
 * Detect query intent for smarter results
 */
export interface QueryIntent {
  type: 'question' | 'action' | 'search';
  entityHint?: 'vehicle' | 'address' | 'insurance' | 'healthcare' | 'finance' | 'reminder' | 'service-provider' | 'utility';
  actionHint?: 'call' | 'email' | 'navigate' | 'show';
  searchTerms: string[];
}

export function detectQueryIntent(query: string): QueryIntent {
  const lower = query.toLowerCase().trim();
  const terms = tokenizeQuery(query);

  // Detect entity type hints
  let entityHint: QueryIntent['entityHint'];
  if (/\b(car|vehicle|auto|vin|license|plate|tesla|toyota|honda|ford|bmw|mercedes|truck|suv)\b/i.test(lower)) {
    entityHint = 'vehicle';
  } else if (/\b(insurance|policy|coverage|premium|claim)\b/i.test(lower)) {
    entityHint = 'insurance';
  } else if (/\b(doctor|dentist|hospital|clinic|medical|health|physician|eye|vision)\b/i.test(lower)) {
    entityHint = 'healthcare';
  } else if (/\b(bank|credit|loan|mortgage|finance|account)\b/i.test(lower)) {
    entityHint = 'finance';
  } else if (/\b(reminder|task|todo|appointment|schedule)\b/i.test(lower)) {
    entityHint = 'reminder';
  } else if (/\b(address|home|house|property|location)\b/i.test(lower)) {
    entityHint = 'address';
  } else if (/\b(plumber|electrician|hvac|contractor|repair|service)\b/i.test(lower)) {
    entityHint = 'service-provider';
  } else if (/\b(electric|water|gas|internet|utility|bill)\b/i.test(lower)) {
    entityHint = 'utility';
  }

  // Detect action hints
  let actionHint: QueryIntent['actionHint'];
  if (/^call\s/i.test(lower) || /\bcall\s+(my\s+)?/i.test(lower)) {
    actionHint = 'call';
  } else if (/^email\s/i.test(lower) || /\bemail\s+(my\s+)?/i.test(lower)) {
    actionHint = 'email';
  } else if (/^(navigate|directions|go)\s/i.test(lower)) {
    actionHint = 'navigate';
  } else if (/^(show|list|find|get)\s/i.test(lower)) {
    actionHint = 'show';
  }

  // Detect query type
  let type: QueryIntent['type'] = 'search';
  if (/^(what|where|when|which|who|how)\s/i.test(lower) || lower.endsWith('?')) {
    type = 'question';
  } else if (actionHint) {
    type = 'action';
  }

  return {
    type,
    entityHint,
    actionHint,
    searchTerms: terms,
  };
}

/**
 * Score and rank search results
 */
export interface RankedResult<T> {
  item: T;
  score: number;
  matchedTerms: string[];
  matchType: SearchMatch['matchType'];
}

export function rankResults<T>(
  results: RankedResult<T>[],
  _intent: QueryIntent
): RankedResult<T>[] {
  return results.sort((a, b) => {
    // Primary: score
    if (Math.abs(a.score - b.score) > 0.1) {
      return b.score - a.score;
    }

    // Secondary: exact matches first
    if (a.matchType !== b.matchType) {
      const typeOrder = { exact: 0, partial: 1, synonym: 2, fuzzy: 3 };
      return typeOrder[a.matchType] - typeOrder[b.matchType];
    }

    // Tertiary: more matched terms
    return b.matchedTerms.length - a.matchedTerms.length;
  });
}
